// assets/pswp-gallery.js
// Module: initializes PhotoSwipe Lightbox (v5) for Dawn media-gallery.
// It will attempt to dynamic-import local esm modules if present, otherwise uses CDN.

const PHOTO_SWIPE_CDN = {
  core: 'https://unpkg.com/photoswipe@5/dist/photoswipe.esm.js',
  lightbox: 'https://unpkg.com/photoswipe@5/dist/photoswipe-lightbox.esm.js',
};

async function ensurePhotoSwipe() {
  // Try to import PhotoSwipe core and Lightbox from local assets (theme assets) first
  // Fallback: import from CDN
  try {
    const pswp = await import('https://unpkg.com/photoswipe@5/dist/photoswipe.esm.js');
    const lightbox = await import('https://unpkg.com/photoswipe@5/dist/photoswipe-lightbox.esm.js');
    return { PhotoSwipe: pswp.default || pswp.PhotoSwipe, PhotoSwipeLightbox: lightbox.default || lightbox.PhotoSwipeLightbox };
  } catch (errLocal) {
    try {
      const pswp = await import(PHOTO_SWIPE_CDN.core);
      const lightbox = await import(PHOTO_SWIPE_CDN.lightbox);
      return { PhotoSwipe: pswp.default || pswp.PhotoSwipe, PhotoSwipeLightbox: lightbox.default || lightbox.PhotoSwipeLightbox };
    } catch (errCDN) {
      console.warn('PhotoSwipe modules could not be loaded.', errCDN);
      return null;
    }
  }
}

function collectItemsFromViewer(viewer) {
  const items = [];
  // prefer slides with data-media-id (main slides)
  viewer.querySelectorAll('[data-media-id]').forEach((slide) => {
    // get element that holds data-pswp attributes (could be slide itself or a nested button)
    const sourceEl = slide.querySelector('[data-pswp-src]') || slide;
    const src = sourceEl?.dataset?.pswpSrc || (sourceEl.querySelector('img')?.src);
    if (!src) return;
    const w = parseInt(sourceEl?.dataset?.pswpWidth || 0, 10) || undefined;
    const h = parseInt(sourceEl?.dataset?.pswpHeight || 0, 10) || undefined;
    const alt = sourceEl.querySelector('img')?.alt || '';
    items.push({ src, w, h, alt, element: slide });
  });

  // fallback to thumbnail buttons if no main slides found
  if (items.length === 0) {
    viewer.querySelectorAll('[data-pswp-src]').forEach((btn) => {
      const src = btn.dataset.pswpSrc || btn.src;
      if (!src) return;
      const w = parseInt(btn.dataset.pswpWidth || 0, 10) || undefined;
      const h = parseInt(btn.dataset.pswpHeight || 0, 10) || undefined;
      const alt = btn.querySelector('img')?.alt || '';
      items.push({ src, w, h, alt, element: btn });
    });
  }
  return items;
}

(async () => {
  const modules = await ensurePhotoSwipe();
  if (!modules) return;
  const { PhotoSwipe, PhotoSwipeLightbox } = modules;

  document.querySelectorAll('media-gallery').forEach((galleryEl) => {
    const viewer = galleryEl.querySelector('[id^="GalleryViewer"]');
    if (!viewer) return;

    // prepare lightbox instance for this gallery
    const lightbox = new PhotoSwipeLightbox({
      gallery: viewer, // just logical reference
      children: '[data-media-id] .product__modal-opener--image, [data-pswp-src]',
      pswpModule: PhotoSwipe,
      showHideAnimationType: 'zoom',
      bgOpacity: 1,
      zoom: false, // No zoom-in on click
      counter: false,
      preloader: false,
    });
    lightbox.init();

    // Click handler: open photoswipe when clicking an image opener
    galleryEl.addEventListener('click', (e) => {
      const btn = e.target.closest('.product__modal-opener--image, [data-pswp-src], [data-media-id]');
      if (!btn) return;

      // If it's a video/model, let existing behavior handle it
      const mediaType = (btn.dataset?.pswpType || btn.closest('[data-pswp-type]')?.dataset?.pswpType || '').toLowerCase();
      if (mediaType && mediaType !== 'image') return;

      e.preventDefault();

      // Build fresh items list and determine index
      const items = collectItemsFromViewer(viewer);
      // Find index by matching element's data-media-id or data-target
      let index = 0;
      const slide = btn.closest('[data-media-id]') || btn.closest('[data-target]') || btn;
      if (slide) {
        const mediaId = slide.dataset.mediaId || slide.dataset.target;
        if (mediaId) {
          const found = items.findIndex((it) => it.element && (it.element.dataset.mediaId === mediaId || it.element.dataset.target === mediaId));
          if (found >= 0) index = found;
        } else {
          // try matching src
          const src = btn.dataset?.pswpSrc || btn.querySelector('img')?.src;
          const found = items.findIndex((it) => it.src === src);
          if (found >= 0) index = found;
        }
      }

      // open using lightbox's loadAndOpen (pass items as dataSource)
      try {
        lightbox.loadAndOpen(index, null, { dataSource: items });
      } catch (err) {
        // fallback: attempt direct PhotoSwipe.open if present
        if (PhotoSwipe && PhotoSwipe.open) {
          PhotoSwipe.open(items, { index });
        } else {
          console.warn('Could not open PhotoSwipe', err);
        }
      }
    });

    // Optional: also open PhotoSwipe when product modal dispatches a modalOpened event
    document.body.addEventListener('modalOpened', (ev) => {
      // When modal opens, open photoswipe on the active slide if we detected one
      const activeSlide = viewer.querySelector('.product__media-item.is-active') || viewer.querySelector('[data-media-id]');
      if (!activeSlide) return;
      const btn = activeSlide.querySelector('.product__modal-opener--image') || activeSlide;
      if (btn) {
        // Simulate a click to reuse behavior above
        btn.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      }
    });
  });
})();
